import { useMemo, useState } from 'react'

import {
  Button,
  Flex,
  IconLinkBreak,
  IconLinkSimple,
  Tag,
  Text,
  Tooltip
} from '@vtex/shoreline'

import Panel from '~/components/Panel'
import { isAdvertiser, isPublisher, isReadOnly } from '~/helpers'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import { useIsManagedByVtex } from '~/modules/retailMedia/hooks/useIsManagedByVtex'

import { useCampaignInsertionOrderMutations } from './hooks/useCampaignInsertionOrderMutations'
import { useCampaignInsertionOrderPanelData } from './hooks/useCampaignInsertionOrderPanelData'
import { CampaignInsertionOrderLinkBlock } from './LinkBlock'
import { CampaignInsertionOrderMetrics } from './Metrics'
import { CampaignInsertionOrderSettings } from './Settings'
import { CampaignInsertionOrderUnlinkModal } from './UnlinkModal'

import styles from './styles.module.css'

type CampaignInsertionOrderPanelProps = {
  campaignId: string
  insertionOrders?: CampaignLinkedInsertionOrder[] | null
}

type UnlinkState = 'hidden' | 'enabled' | 'lastInsertionOrder'

type CampaignInsertionOrderPanelItemProps = {
  insertionOrder: CampaignLinkedInsertionOrder
  unlinkState: UnlinkState
  isPending: boolean
  onUnlink: (insertionOrder: CampaignLinkedInsertionOrder) => void
}

const CampaignInsertionOrderPanelItem = ({
  insertionOrder,
  unlinkState,
  isPending,
  onUnlink
}: CampaignInsertionOrderPanelItemProps) => {
  const { t } = useAPPTranslation()
  const panelData = useCampaignInsertionOrderPanelData(insertionOrder)

  if (!panelData) return null

  const { name, period, showPendingBadge, metrics, settings } = panelData

  const isLastInsertionOrder = unlinkState === 'lastInsertionOrder'

  // aria-disabled keeps the last PI's button focusable so the tooltip explains why.
  // Shoreline's Button is Ariakit's, which turns aria-disabled into a native
  // disabled unless accessibleWhenDisabled is set.
  const unlinkButton = (
    <Button
      variant="tertiary"
      className={styles.unlink}
      aria-disabled={isLastInsertionOrder || undefined}
      {...(isLastInsertionOrder ? { accessibleWhenDisabled: true } : {})}
      disabled={isPending}
      onClick={() => {
        if (!isLastInsertionOrder) onUnlink(insertionOrder)
      }}
      data-testid="campaign-io-unlink"
    >
      <IconLinkBreak />
      {t('rm:campaign.detail.insertionOrder.actions.unlink')}
    </Button>
  )

  return (
    <div className={styles.summary} data-testid="campaign-io-summary">
      <div className={styles.headerRow}>
        <div className={styles.header}>
          <Flex align="center" gap="$space-2" style={{ flexWrap: 'wrap' }}>
            <Text
              as="div"
              variant="emphasis"
              className={styles.name}
              data-testid="campaign-io-name"
            >
              {name}
            </Text>
            {showPendingBadge ? (
              <Tag
                color="yellow"
                variant="secondary"
                data-testid="campaign-io-document-status-pending"
              >
                {t('rm:campaign.insertionOrder.documentStatus.pending')}
              </Tag>
            ) : null}
          </Flex>
          <Text
            as="div"
            variant="caption1"
            color="var(--sl-fg-base-soft)"
            data-testid="campaign-io-period"
          >
            {period}
          </Text>
        </div>

        {unlinkState !== 'hidden' &&
          (isLastInsertionOrder ? (
            <Tooltip
              label={t('rm:campaign.detail.insertionOrder.unlinkLastTooltip')}
            >
              {unlinkButton}
            </Tooltip>
          ) : (
            unlinkButton
          ))}
      </div>

      <CampaignInsertionOrderMetrics metrics={metrics} />
      <CampaignInsertionOrderSettings settings={settings} />
    </div>
  )
}

export const CampaignInsertionOrderPanel = ({
  campaignId,
  insertionOrders
}: CampaignInsertionOrderPanelProps) => {
  const { t } = useAPPTranslation()
  const { isManagedByVtex } = useIsManagedByVtex()

  const isManagedAdvertiser = isAdvertiser() && isManagedByVtex
  const canEdit = isManagedAdvertiser && !isReadOnly()

  const [isPanelOpen, setIsPanelOpen] = useState(true)
  const [isLinkBlockOpen, setIsLinkBlockOpen] = useState(false)
  const [insertionOrderToUnlink, setInsertionOrderToUnlink] =
    useState<CampaignLinkedInsertionOrder | null>(null)
  // Unlinks already done here. The list only changes when the background
  // reload returns (or never, if it fails), so until then they neither count
  // toward the last-PI rule nor offer "Desvincular" again. No reset needed:
  // once the reload drops a PI, its id stops matching anything.
  const [unlinkedIds, setUnlinkedIds] = useState<string[]>([])

  const { link, unlink, isPending } =
    useCampaignInsertionOrderMutations(campaignId)

  const items = useMemo(() => insertionOrders ?? [], [insertionOrders])
  const linkedIds = useMemo(() => items.map(item => item.id), [items])

  if (isPublisher()) return null
  if (!items.length && !isManagedAdvertiser) return null

  const isUnlinked = (insertionOrder: CampaignLinkedInsertionOrder) =>
    !!insertionOrder.campaign_checking_account_id &&
    unlinkedIds.includes(insertionOrder.campaign_checking_account_id)

  const remainingCount = items.filter(
    insertionOrder => !isUnlinked(insertionOrder)
  ).length

  const getUnlinkState = (
    insertionOrder: CampaignLinkedInsertionOrder
  ): UnlinkState => {
    if (
      !canEdit ||
      !insertionOrder.campaign_checking_account_id ||
      isUnlinked(insertionOrder)
    ) {
      return 'hidden'
    }

    return remainingCount === 1 ? 'lastInsertionOrder' : 'enabled'
  }

  const handleOpenLinkBlock = () => {
    if (isPanelOpen) {
      setIsLinkBlockOpen(true)
      return
    }

    // The block lives in the panel body. Mounting it while the Collapse is
    // still closed (display: none) would lose the picker's autoFocus.
    setIsPanelOpen(true)
    requestAnimationFrame(() => setIsLinkBlockOpen(true))
  }

  const handleLink = (insertionOrder: InsertionOrder) => {
    if (isPending) return

    link.mutate(insertionOrder.checking_account_id, {
      onSuccess: () => setIsLinkBlockOpen(false)
    })
  }

  const handleConfirmUnlink = () => {
    const campaignCheckingAccountId =
      insertionOrderToUnlink?.campaign_checking_account_id

    if (isPending || !campaignCheckingAccountId) return

    unlink.mutate(campaignCheckingAccountId, {
      onSuccess: () => {
        setUnlinkedIds(ids => [...ids, campaignCheckingAccountId])
        setInsertionOrderToUnlink(null)
      }
    })
  }

  const headerSideContent = canEdit ? (
    <Button
      variant="secondary"
      onClick={event => {
        // The Panel header toggles the collapse on click
        event.stopPropagation()
        handleOpenLinkBlock()
      }}
      disabled={isLinkBlockOpen || isPending}
      data-testid="campaign-io-link"
    >
      <IconLinkSimple />
      {t('rm:campaign.detail.insertionOrder.actions.link')}
    </Button>
  ) : null

  const linkBlock =
    canEdit && isLinkBlockOpen ? (
      <CampaignInsertionOrderLinkBlock
        linkedIds={linkedIds}
        isLinking={link.isPending}
        isPending={isPending}
        onLink={handleLink}
        onCancel={() => setIsLinkBlockOpen(false)}
      />
    ) : null

  return (
    <Panel
      title={
        <Flex align="center" gap="$space-2">
          <span>{t('rm:campaign.detail.insertionOrder.titlePlural')}</span>
          <Tag color="gray" variant="secondary" data-testid="campaign-io-count">
            {items.length}
          </Tag>
        </Flex>
      }
      headerSideContent={headerSideContent}
      template="shoreline"
      isCollapsible
      open={isPanelOpen}
      onChangeOpen={setIsPanelOpen}
      data-testid="campaign-insertion-order-panel"
    >
      {items.length ? (
        <div className={styles.list}>
          {items.map(insertionOrder => (
            <CampaignInsertionOrderPanelItem
              key={insertionOrder.id}
              insertionOrder={insertionOrder}
              unlinkState={getUnlinkState(insertionOrder)}
              isPending={isPending}
              onUnlink={setInsertionOrderToUnlink}
            />
          ))}

          {linkBlock}
        </div>
      ) : (
        <>
          <div className={styles.empty} data-testid="campaign-io-empty">
            <Text as="div" variant="emphasis">
              {t('rm:campaign.detail.insertionOrder.empty.title')}
            </Text>
            <Text as="div" variant="body" color="var(--sl-fg-base-soft)">
              {t('rm:campaign.detail.insertionOrder.empty.description')}
            </Text>
          </div>
          {linkBlock}
        </>
      )}

      {canEdit ? (
        <CampaignInsertionOrderUnlinkModal
          insertionOrder={insertionOrderToUnlink}
          isUnlinking={unlink.isPending}
          onConfirm={handleConfirmUnlink}
          onClose={() => setInsertionOrderToUnlink(null)}
        />
      ) : null}
    </Panel>
  )
}
